import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Check, Loader2 } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ONE_TIME = [
    {
        key: "founding_25",
        price: "$25",
        name: "Founding Member",
        desc: "Carry the Founding Supporter badge forever.",
        perks: ["Name on the supporter wall", "Founding Supporter badge", "Priority votes on new features"],
        featured: true,
        badge: "Most loved",
    },
    {
        key: "premium_12m_50",
        price: "$50",
        name: "12 Months Premium",
        desc: "A full year of Premium, included at launch.",
        perks: ["Everything in Founding Member", "1 year of Premium included"],
    },
    {
        key: "lifetime_100",
        price: "$100+",
        name: "Lifetime Premium",
        desc: "Pay once. Preserve your legacy forever.",
        perks: ["Everything above", "Lifetime Premium access", "Founding family crest"],
    },
];

const MONTHLY = [
    {
        key: "monthly_10",
        price: "$10",
        name: "Seed Supporter",
        desc: "Steady monthly support that keeps us growing.",
        perks: ["Early Access tier", "Monthly behind-the-scenes letter"],
    },
    {
        key: "monthly_25",
        price: "$25",
        name: "Roots Supporter",
        desc: "The heart of the Founding Circle.",
        perks: ["Founding Member tier", "Founding Supporter badge", "Priority votes on new features"],
        featured: true,
        badge: "Most popular",
    },
    {
        key: "monthly_50",
        price: "$50",
        name: "Family Circle",
        desc: "A full year of Premium, renewed every year.",
        perks: ["12m Premium tier", "Everything in Roots Supporter"],
    },
    {
        key: "monthly_100",
        price: "$100",
        name: "Legacy Keeper",
        desc: "Lifetime Premium, powered by you.",
        perks: ["Lifetime Premium tier", "Founding family crest", "Quarterly founder calls"],
    },
];

function TierCard({ tier, onSupport, loading }) {
    const inner = (
        <div className="flex h-full flex-col rounded-3xl bg-cream p-7">
            {tier.badge && (
                <span className="btn-gradient mb-4 w-fit rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-widest text-white">
                    {tier.badge}
                </span>
            )}
            <p className="font-display text-4xl tracking-tight">
                {tier.price}
                {tier.key.startsWith("monthly") && <span className="text-base text-navy"> /month</span>}
            </p>
            <h3 className="mt-3 font-display text-xl">{tier.name}</h3>
            <p className="mt-2 text-sm leading-relaxed text-navy">{tier.desc}</p>
            <ul className="mt-5 flex-1 space-y-2.5">
                {tier.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm font-medium text-navy">
                        <Check size={15} className="mt-0.5 shrink-0 text-navy/70" />
                        {p}
                    </li>
                ))}
            </ul>
            <button
                data-testid={`support-cta-${tier.key}`}
                onClick={() => onSupport({ lookup_key: tier.key })}
                disabled={loading === tier.key}
                className={`mt-7 flex w-full items-center justify-center gap-2 rounded-full py-3 text-sm font-semibold transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60 ${
                    tier.featured ? "btn-gradient font-semibold text-white" : "border-2 border-warmgray text-navy hover:border-peach"
                }`}
            >
                {loading === tier.key && <Loader2 size={15} className="animate-spin" />}
                Support {tier.price}
            </button>
        </div>
    );

    return (
        <motion.div
            data-testid={`support-card-${tier.key}`}
            initial={{ opacity: 0, y: 32 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
            className={`h-full transition-transform duration-300 hover:-translate-y-2 ${
                tier.featured
                    ? "rounded-3xl bg-gradient-to-br from-lavender to-peach p-[1.5px] shadow-xl shadow-navy/10"
                    : "rounded-3xl border border-warmgray bg-cream shadow-sm"
            }`}
        >
            {inner}
        </motion.div>
    );
}

export default function Support() {
    const [mode, setMode] = useState("one-time");
    const [stats, setStats] = useState({ total_raised: 0, supporter_count: 0, goal: 25000 });
    const [customAmount, setCustomAmount] = useState("");
    const [loading, setLoading] = useState(null);

    useEffect(() => {
        axios
            .get(`${API}/support/stats`)
            .then((r) => setStats(r.data))
            .catch(() => {});
    }, []);

    const startCheckout = async (payload) => {
        const id = payload.lookup_key || "custom";
        setLoading(id);
        try {
            const { data } = await axios.post(`${API}/support/checkout-session`, {
                ...payload,
                origin_url: window.location.origin,
            });
            window.location.href = data.checkout_url;
        } catch (e) {
            toast.error(e.response?.data?.detail || "Could not start checkout. Please try again.");
            setLoading(null);
        }
    };

    const submitCustom = () => {
        const amt = parseFloat(customAmount);
        const min = mode === "monthly" ? 10 : 1;
        if (!amt || amt < min) {
            toast.error(mode === "monthly" ? "Monthly support starts at $10." : "Enter an amount of at least $1.");
            return;
        }
        startCheckout({ custom_amount: amt, is_monthly: mode === "monthly" });
    };

    const pct = Math.min(100, (stats.total_raised / stats.goal) * 100);
    const tiers = mode === "one-time" ? ONE_TIME : MONTHLY;

    return (
        <section id="support" data-testid="support-section" className="border-y border-warmgray bg-ivory py-28 md:py-36">
            <div className="mx-auto max-w-7xl px-6 lg:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 32 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.9, ease: [0.76, 0, 0.24, 1] }}
                    className="max-w-3xl"
                >
                    <p className="mb-4 font-mono text-xs font-semibold uppercase tracking-[0.35em] text-[#C98A94]">Chapter 03 — The Founding Circle</p>
                    <h2 className="font-display text-3xl leading-tight tracking-tight md:text-5xl">
                        Help us build the home <span className="italic text-navy">your memories deserve.</span>
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-navy">
                        Ago GenApp is independently built and funded by the families who believe in it. Every contribution — one-time or monthly — goes directly to building a private, permanent home for your family’s story.
                    </p>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.8, delay: 0.1 }}
                    className="mt-14 rounded-3xl border border-warmgray bg-cream p-7 md:p-9"
                >
                    <div className="flex flex-wrap items-end justify-between gap-4">
                        <div>
                            <p data-testid="progress-raised" className="font-display text-3xl md:text-4xl">
                                ${stats.total_raised.toLocaleString(undefined, { minimumFractionDigits: 0 })}
                                <span className="ml-2 text-base text-navy">of ${stats.goal.toLocaleString()} goal</span>
                            </p>
                        </div>
                        <p data-testid="progress-count" className="font-mono text-xs font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                            {stats.supporter_count} founding {stats.supporter_count === 1 ? "supporter" : "supporters"}
                        </p>
                    </div>
                    <div data-testid="progress-bar" className="mt-5 h-2.5 w-full overflow-hidden rounded-full bg-warmgray">
                        <motion.div
                            initial={{ width: 0 }}
                            whileInView={{ width: `${Math.max(pct, 2)}%` }}
                            viewport={{ once: true }}
                            transition={{ duration: 1.4, ease: [0.76, 0, 0.24, 1], delay: 0.2 }}
                            className="h-full rounded-full bg-gradient-to-r from-lavender to-peach"
                        />
                    </div>
                </motion.div>

                <div className="mt-14 flex items-center gap-2">
                    {[
                        { id: "one-time", label: "One-time" },
                        { id: "monthly", label: "Monthly" },
                    ].map((t) => (
                        <button
                            key={t.id}
                            data-testid={`toggle-${t.id}`}
                            onClick={() => setMode(t.id)}
                            className={`rounded-full px-6 py-2.5 text-sm font-medium transition-colors duration-300 ${
                                mode === t.id ? "btn-gradient text-white" : "border-2 border-navy text-navy hover:bg-navy/5"
                            }`}
                        >
                            {t.label}
                        </button>
                    ))}
                    {mode === "monthly" && (
                        <span className="ml-2 font-mono text-[11px] font-semibold uppercase tracking-widest text-[#C98A94]">Cancel anytime</span>
                    )}
                </div>

                <div className={`mt-10 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3`}>
                    {tiers.map((tier) => (
                        <TierCard key={tier.key} tier={tier} onSupport={startCheckout} loading={loading} />
                    ))}
                    {(
                        <motion.div
                            data-testid="support-card-custom"
                            initial={{ opacity: 0, y: 32 }}
                            whileInView={{ opacity: 1, y: 0 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{ duration: 0.7, ease: [0.76, 0, 0.24, 1] }}
                            className="flex h-full flex-col rounded-3xl border border-dashed border-lavender/50 bg-cream p-7 transition-transform duration-300 hover:-translate-y-2"
                        >
                            <p className="font-display text-4xl tracking-tight italic text-navy">Custom</p>
                            <h3 className="mt-3 font-display text-xl">Your own amount</h3>
                            <p className="mt-2 flex-1 text-sm leading-relaxed text-navy">
                                {mode === "monthly"
                                    ? "Give what feels right each month — from $10 up, no maximum. $25+ earns Founding Member, $50+ earns 12m Premium, $100+ earns Lifetime."
                                    : "Every dollar helps. Give what feels right — $25+ earns Founding Member, $50+ earns 12 months Premium, $100+ earns Lifetime."}
                            </p>
                            <div className="mt-6 flex items-center gap-2">
                                <div className="relative flex-1">
                                    <span className="absolute left-4 top-1/2 -translate-y-1/2 text-sm font-medium text-navy">$</span>
                                    <input
                                        data-testid="custom-amount-input"
                                        type="number"
                                        min={mode === "monthly" ? "10" : "1"}
                                        value={customAmount}
                                        onChange={(e) => setCustomAmount(e.target.value)}
                                        placeholder="25"
                                        className="w-full rounded-full border-2 border-warmgray bg-[#FDFBF9] py-3 pl-8 pr-4 text-sm font-medium text-navy outline-none transition-colors duration-300 focus:border-peach"
                                    />
                                </div>
                                <button
                                    data-testid="custom-amount-button"
                                    onClick={submitCustom}
                                    disabled={loading === "custom"}
                                    className="btn-gradient flex items-center gap-2 rounded-full px-5 py-3 text-sm font-semibold text-white transition-transform duration-300 hover:-translate-y-0.5 disabled:opacity-60"
                                >
                                    {loading === "custom" && <Loader2 size={15} className="animate-spin" />}
                                    Give
                                </button>
                            </div>
                        </motion.div>
                    )}
                </div>

                <p className="mt-10 text-center font-mono text-[11px] font-semibold uppercase tracking-[0.25em] text-[#C98A94]">
                    Secure checkout by Stripe
                </p>
            </div>
        </section>
    );
}
